import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
    /** The page on screen, numbered from 1. */
    page: number;
    /** How many there are, when known; the public library only knows if one follows. */
    totalPages?: number;
    hasNext: boolean;
    onChange: (page: number) => void;
    /** What is being paged through, for assistive technology. */
    label?: string;
};

/**
 * Previous and next through a library ten at a time — meditations or music —
 * and which page this is. Nothing at all when everything fits on one:
 * controls that can only be disabled are noise.
 */
export default function MeditationPager({ page, totalPages, hasNext, onChange, label }: Props) {
    const { t } = useTranslation();

    if (page === 1 && !hasNext) return null;

    const buttonClasses =
        "flex min-h-11 items-center gap-1 rounded-lg border border-border-subtle bg-surface-raised px-3 text-sm font-medium text-content-secondary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50";

    return (
        <nav
            aria-label={label ?? t("meditation.pager.label")}
            className="mt-4 flex items-center justify-between gap-3"
        >
            <button
                type="button"
                className={buttonClasses}
                disabled={page === 1}
                onClick={() => onChange(page - 1)}
            >
                <ChevronLeft size={16} aria-hidden="true" />
                {t("meditation.pager.previous")}
            </button>
            <span className="text-sm text-content-tertiary" aria-live="polite">
                {totalPages
                    ? t("meditation.pager.pageOf", { page, total: totalPages })
                    : t("meditation.pager.page", { page })}
            </span>
            <button
                type="button"
                className={buttonClasses}
                disabled={!hasNext}
                onClick={() => onChange(page + 1)}
            >
                {t("meditation.pager.next")}
                <ChevronRight size={16} aria-hidden="true" />
            </button>
        </nav>
    );
}
